import {canCardBeInQueue} from "@cdx/shared/queue/queue-rules";
import {INVALIDATE} from "../mate/mate-shared";
import type {Tx} from "../mate/mate-types";
import type {AccountId} from "../../cdx-models/Account";
import type {CardId} from "../../cdx-models/Card";
import type {QueueEntryId} from "../../cdx-models/QueueEntry";
import type {UserId} from "../../cdx-models/User";
import {implCreate, implOptimistic, implUpdate, type ImplicitUpdate} from "./types";

export const handUpdates: ImplicitUpdate[] = [
  implCreate("queueEntry"),
  implUpdate("queueEntry", {sortIndex: INVALIDATE}),
];

type HandEntry = {id: QueueEntryId; card: CardId; user: UserId; sortIndex: number};

/** Every open hand entry of the organization, the list `getUserQueueCardIds` reads. */
const readHands = (tx: Tx, accountId: AccountId) =>
  tx.find("account", accountId, "queueEntries", {cardDoneAt: null}) as unknown as HandEntry[];

const currentAccountId = (tx: Tx): AccountId => tx.get("_root", "_root", "account");

/** Takes the cards out of the user's hand; without `userId`, out of every hand, as the server does. */
export const removeFromHandsOptimistically = ({
  cardIds,
  userId,
}: {
  cardIds: CardId[];
  userId?: UserId;
}) =>
  implOptimistic((tx) => {
    const cardIdSet = new Set(cardIds);
    for (const entry of readHands(tx, currentAccountId(tx))) {
      if (!cardIdSet.has(entry.card)) continue;
      if (userId && entry.user !== userId) continue;
      tx.delete("queueEntry", entry.id);
    }
  });

/**
 * The server's card hooks (node-api `queue/QueueEntry.ts`) for a card update: a deleted card, or an
 * archived one that isn't done, leaves every hand. A card becoming done stays in its owner's hand as
 * a done entry and leaves the others. Neither happens to a card without an owner.
 */
export const cardLeavesHandsOptimistically = ({
  cardId,
  wasDone,
}: {
  cardId: CardId;
  wasDone: boolean;
}) =>
  implOptimistic((tx) => {
    const ownerId = tx.get<UserId | null>("card", cardId, "assignee");
    if (!ownerId) return;
    const status = tx.get<string>("card", cardId, "status");
    const visibility = tx.get<string>("card", cardId, "visibility");
    const leavesAll = visibility === "deleted" || (visibility === "archived" && status !== "done");
    const becameDone = status === "done" && !wasDone;
    if (!leavesAll && !becameDone) return;
    for (const entry of readHands(tx, currentAccountId(tx))) {
      if (entry.card !== cardId) continue;
      if (!leavesAll && entry.user === ownerId) {
        tx.update("queueEntry", entry.id, {cardDoneAt: new Date(), sortIndex: null});
      } else {
        tx.delete("queueEntry", entry.id);
      }
    }
  });

type Placement =
  // `handQueue.addCardsToHand`: new cards go to the end, the others keep their spot
  | {type: "append"; cardIds: CardId[]}
  // `handQueue.setCardOrders`: the hand takes this order, dragged and left out cards follow
  | {type: "order"; cardIds: CardId[]; draggedCardIds: CardId[]};

type CreatedEntry = {id: QueueEntryId; cardId: CardId};

/** Shows the user's hand the way the server will leave it: the same cards, at the same positions. */
export const placeInHandOptimistically = ({
  userId,
  placement,
}: {
  userId: UserId;
  placement: Placement;
}) =>
  implOptimistic(
    (tx) => {
      const accountId = currentAccountId(tx);
      const entryByCardId = new Map<CardId, HandEntry>();
      let lastSortIndex = -1;
      for (const entry of readHands(tx, accountId)) {
        if (entry.user !== userId) continue;
        entryByCardId.set(entry.card, entry);
        lastSortIndex = Math.max(lastSortIndex, entry.sortIndex ?? -1);
      }
      const mayJoin = (cardId: CardId) => {
        const status = tx.get<string>("card", cardId, "status", "");
        const visibility = tx.get<string>("card", cardId, "visibility", "");
        if (!canCardBeInQueue({status, visibility})) return false;
        // adding skips done cards, a drag keeps them
        return placement.type === "order" || status !== "done";
      };

      const getPositions = () => {
        if (placement.type === "append") {
          return [...new Set(placement.cardIds)]
            .filter((cardId) => !entryByCardId.has(cardId) && mayJoin(cardId))
            .map((cardId) => ({cardId, sortIndex: (lastSortIndex += 1)}));
        }
        const order = [...new Set(placement.cardIds)];
        const inOrder = new Set(order);
        for (const cardId of [...placement.draggedCardIds, ...entryByCardId.keys()]) {
          if (!inOrder.has(cardId)) {
            inOrder.add(cardId);
            order.push(cardId);
          }
        }
        return order
          .filter((cardId) => entryByCardId.has(cardId) || mayJoin(cardId))
          .map((cardId, sortIndex) => ({cardId, sortIndex}));
      };

      for (const {cardId, sortIndex} of getPositions()) {
        const existing = entryByCardId.get(cardId);
        if (existing) {
          if (existing.sortIndex !== sortIndex) tx.update("queueEntry", existing.id, {sortIndex});
        } else {
          tx.create(
            "queueEntry",
            {card: cardId, user: userId, account: accountId, sortIndex, cardDoneAt: null},
            {key: cardId}
          );
        }
      }
    },
    {
      createdIds: (retVal: {queueEntries?: CreatedEntry[]} | undefined) => ({
        queueEntry: Object.fromEntries((retVal?.queueEntries ?? []).map((e) => [e.cardId, e.id])),
      }),
    }
  );
